import {faChartColumn} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {nameForAnalytics} from '@rdcomponents/navigation/navigation-type';
import {FC, useCallback} from 'react';

import {appRoute} from '../../../../app-route';
import {OnlinePresenceAnalyticsActivation} from '../../../../page/main/online-presence/online-presence-analytics-activation';
import {OnlinePresenceRouting} from '../../../../page/main/online-presence/online-presence-routing';
import {useCatalogInfo, usePaidCatalogs} from '../../../../provider/catalogs/catalogs-hook';
import {useLicenses} from '../../../../provider/license/license-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {useUser} from '../../../../provider/user/user-hook';
import {OnlinePresenceV2FilterQueriesEnum} from '../../../../service/online-presence/online-presence-type';
import {FeaturesEnum} from '../../../../service/user/user-type';
import {ProvidersIdsEnum} from '../../../../util/type';
import {getFeatureOrLandingPage, getMenuItemNameForAnalytics} from '../../../navigation/navigation-helper';
import {NavigationItemConfigType} from '../routing-type';

export function useOnlinePresenceNavigation(): Array<NavigationItemConfigType> {
    const {user} = useUser();
    const {isCatalogPaid} = usePaidCatalogs();
    const {licenses, availableFeatures, enabledFeatures} = useLicenses();
    const {getLocalizedString} = useLocale();
    const {getCatalogName} = useCatalogInfo();

    const {regionAvailableCatalogs} = user || {};

    const onlinePresenceQueries = Object.values(OnlinePresenceV2FilterQueriesEnum);

    function isCatalogAvailable(providerId: ProvidersIdsEnum): boolean {
        return Boolean(regionAvailableCatalogs?.[providerId] && isCatalogPaid(providerId));
    }

    const getOnlinePresenceFeatureOrLandingPage = useCallback(
        (pageComponent: FC<Record<string, unknown>>) => {
            return getFeatureOrLandingPage({
                license: licenses[FeaturesEnum.onlinePresenceAnalytics],
                activationComponent: OnlinePresenceAnalyticsActivation,
                pageComponent,
            });
        },
        [licenses]
    );

    return [
        {
            isAvailable:
                Boolean(user) &&
                availableFeatures[FeaturesEnum.onlinePresenceAnalytics] &&
                enabledFeatures.includes(FeaturesEnum.onlinePresenceAnalytics),
            isActivated: licenses[FeaturesEnum.onlinePresenceAnalytics]?.isActive,
            path: appRoute.onlinePresence.path,
            icon: <FontAwesomeIcon icon={faChartColumn} />,
            label: getLocalizedString('CATEGORY_NAME__ONLINE_PRESENCE_ANALYTICS'),
            [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ONLINE_PRESENCE_ANALYTICS'),
            exact: false,
            deniedPath: [appRoute.onlinePresence.path],
            redirectPath: appRoute.onlinePresenceDashboard.path,
            component: getOnlinePresenceFeatureOrLandingPage(OnlinePresenceRouting),
            // todo: остальные роуты находятся в родительском компоненте OnlinePresenceRouting
            children: [
                {
                    allowedSearchParameters: onlinePresenceQueries,
                    path: appRoute.onlinePresenceDashboard.path,
                    label: getLocalizedString('CATEGORY_NAME__ONLINE_PRESENCE__DASHBOARD'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ONLINE_PRESENCE__DASHBOARD'),
                },
                {
                    allowedSearchParameters: onlinePresenceQueries,
                    path: appRoute.onlinePresenceSynchronization.path,
                    label: getLocalizedString('CATEGORY_NAME__ONLINE_PRESENCE__SYNCHRONIZATION'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ONLINE_PRESENCE__SYNCHRONIZATION'),
                },
                {
                    allowedSearchParameters: onlinePresenceQueries,
                    isAvailable: isCatalogAvailable(ProvidersIdsEnum.google),
                    path: appRoute.onlinePresenceGoogle.path,
                    label: getLocalizedString('CATEGORY_NAME__ONLINE_PRESENCE__GOOGLE'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ONLINE_PRESENCE__GOOGLE'),
                },
                {
                    allowedSearchParameters: onlinePresenceQueries,
                    isAvailable: isCatalogAvailable(ProvidersIdsEnum.yandex),
                    path: appRoute.onlinePresenceYandex.path,
                    label: getLocalizedString('CATEGORY_NAME__ONLINE_PRESENCE__YANDEX'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ONLINE_PRESENCE__YANDEX'),
                },
                {
                    allowedSearchParameters: onlinePresenceQueries,
                    isAvailable: isCatalogAvailable(ProvidersIdsEnum.doubleGis),
                    path: appRoute.onlinePresenceWgis.path,
                    label: getLocalizedString('CATEGORY_NAME__ONLINE_PRESENCE__WGIS'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ONLINE_PRESENCE__WGIS'),
                },
                {
                    allowedSearchParameters: onlinePresenceQueries,
                    isAvailable: isCatalogAvailable(ProvidersIdsEnum.facebook),
                    path: appRoute.onlinePresenceFacebook.path,
                    label: getLocalizedString('CATEGORY_NAME__ONLINE_PRESENCE__FACEBOOK', {
                        catalogName: getCatalogName(ProvidersIdsEnum.facebook) || 'FB',
                    }),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ONLINE_PRESENCE__FACEBOOK', {
                        catalogName: getCatalogName(ProvidersIdsEnum.facebook) || 'FB',
                    }),
                },
                {
                    allowedSearchParameters: onlinePresenceQueries,
                    path: appRoute.onlinePresencePhrases.path,
                    label: getLocalizedString('CATEGORY_NAME__ONLINE_PRESENCE__PHRASES'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ONLINE_PRESENCE__PHRASES'),
                },
                {
                    allowedSearchParameters: onlinePresenceQueries,
                    path: appRoute.onlinePresenceLocations.path,
                    label: getLocalizedString('CATEGORY_NAME__ONLINE_PRESENCE__LOCATIONS'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ONLINE_PRESENCE__LOCATIONS'),
                },
                {
                    allowedSearchParameters: onlinePresenceQueries,
                    isAvailable: user?.isStaff,
                    path: appRoute.onlinePresencePro.path,
                    label: getLocalizedString('CATEGORY_NAME__ONLINE_PRESENCE__ANALYTICS_PRO'),
                    [nameForAnalytics]: getMenuItemNameForAnalytics('CATEGORY_NAME__ONLINE_PRESENCE__ANALYTICS_PRO'),
                },
            ],
        },
    ];
}
